// Outcome utilities — determine better/worse outcomes from actual vs expected.

/**
 * Extract a numeric value from a figure string (e.g. "+0.5%" -> 0.5,
 * "-15.0" -> -15.0). Returns undefined when no number is present.
 */
export function numberFrom(text: string | null | undefined): number | undefined {
  if (text == null || text === "") return undefined;
  const matches = String(text).match(/(\+?-?\d+(\.\d+)?)/);
  if (matches) {
    return parseFloat(matches[0]);
  }
  return undefined;
}

export type Outcome = "better" | "worse" | "same" | "unknown";

/**
 * Determine outcome classification based on actual vs expected values.
 * `inverted` flips the interpretation (a higher figure is worse).
 */
export function determineOutcome(
  actual: string | null | undefined,
  expected: string | null | undefined,
  inverted: boolean | null | undefined
): Outcome {
  const act = numberFrom(actual);
  const exp = numberFrom(expected);

  // Need both values to determine an outcome.
  if (act === undefined || exp === undefined) {
    return "unknown";
  }

  if (act < exp) {
    // Lower is worse for normal, better for inverted.
    return inverted ? "better" : "worse";
  }
  if (act > exp) {
    // Higher is better for normal, worse for inverted.
    return inverted ? "worse" : "better";
  }
  return "same";
}

/**
 * Get CSS classes for outcome styling
 * (e.g. "outcome-better direction-uninverted").
 */
export function getOutcomeClasses(
  actual: string | null | undefined,
  expected: string | null | undefined,
  inverted: boolean | null | undefined
): string {
  const outcome = determineOutcome(actual, expected, inverted);

  if (outcome === "unknown") {
    return "outcome-unknown";
  }

  const directionClass = inverted ? "direction-inverted" : "direction-uninverted";
  return `outcome-${outcome} ${directionClass}`;
}

/**
 * Format a figure value with optional currency, unit and multiplier.
 * Returns "-" for empty values.
 */
export function formatFigure(
  value: string | null | undefined,
  currency: string | null | undefined,
  unit: string | null | undefined,
  multiplier: string | null | undefined = undefined
): string {
  if (value == null || String(value).trim() === "") {
    return "-";
  }

  const cleanUnit = String(unit ?? "")
    .replace("Percent", "%")
    .replace("Index", "")
    .replace("Person", "")
    .replace("No. of", "")
    .replace("Balance", "")
    .trim();

  const parts: string[] = [];
  if (currency) parts.push(currency);
  parts.push(String(value));
  if (multiplier) parts.push(multiplier);
  if (cleanUnit) parts.push(cleanUnit);

  return parts.join("");
}
